// The left side of Items built and the walk: the View (the sheet at its signed URL, every item numbered on it by its
// mark), its head (the walls, Place marks and Replace sheet as plain buttons since the Views pack of 2026-10-24: kind,
// Overview and Primary live on the Files tab's Views table), the placing tray (what is being placed and how; what a
// replaced sheet could not carry) and the picture. Tap a mark to open its item. While placing is on, a mark can be
// dragged, a click places the open item as a point, and a press-and-drag on the sheet marks a region (V9): the region
// is the clickable area for its item. An alternate has no mark of its own: it is drawn dashed where the item it stands in
// for sits (V8). On the walk a checked item's mark carries a green circle, the next stop a "next" flag, and an item the
// open build does not carry is hollow and grey. Nothing is edited on the picture but the marks themselves.

import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { carryMarks, isOverview, type ResolvedItem, type ViewKind } from '@zerogap/engine';
import { deleteRow, insertRow, insertRows, toast, updateRow, upsertRow, useRows, type Row } from '../../../lib/api';
import { useSession } from '../../../lib/session';
import { pressable } from '../../../ui/kit';
import { uploadFile, useSignedUrl } from '../Files';
import type { JobCtx } from '../Workspace';
import { wallShort, type WalkModel } from './model';

/**
 * An SVG whose root carries no width and height has no size of its own in an <img>, so it showed as nothing (the three Maha views
 * filed 2026-10-23); when its viewBox says the shape, the picture takes that aspect ratio (2026-10-23). The connector now writes
 * width and height on the way in; this covers the files already stored, and any uploaded by hand.
 */
export function svgAspect(text: string): number | null {
  const open = /<svg\b[^>]*>/i.exec(text.slice(0, 65536));
  if (!open) return null;
  const attr = (name: string) => new RegExp(`\\s${name}\\s*=\\s*["']([^"']*)["']`, 'i').exec(open[0])?.[1] ?? null;
  if (attr('width') && attr('height')) return null;
  const vb = attr('viewBox')?.trim().split(/[\s,]+/).map(Number);
  if (!vb || vb.length !== 4 || vb.some((n) => !Number.isFinite(n)) || vb[2]! <= 0 || vb[3]! <= 0) return null;
  return vb[2]! / vb[3]!;
}
/** True when an SVG would show as nothing: no width and height on its root, and no viewBox to size it from (the Views row's "shows blank"). */
export function svgBlank(text: string): boolean {
  const open = /<svg\b[^>]*>/i.exec(text.slice(0, 65536));
  if (!open) return true;
  const attr = (name: string) => new RegExp(`\\s${name}\\s*=\\s*["']([^"']*)["']`, 'i').exec(open[0])?.[1] ?? null;
  if (attr('width') && attr('height')) return false;
  return svgAspect(text) === null;
}
function useSvgAspect(url: string | null, isSvg: boolean): number | null {
  const q = useQuery({
    queryKey: ['svg-aspect', url],
    enabled: !!url && isSvg,
    staleTime: 50 * 60_000,
    queryFn: async () => svgAspect(await (await fetch(url!)).text()),
  });
  return isSvg ? q.data ?? null : null;
}
/** Whether a stored SVG shows blank (the Views table's mark on the row). Null while unknown or not an SVG. */
export function useSvgBlank(url: string | null, isSvg: boolean): boolean | null {
  const q = useQuery({
    queryKey: ['svg-blank', url],
    enabled: !!url && isSvg,
    staleTime: 50 * 60_000,
    queryFn: async () => svgBlank(await (await fetch(url!)).text()),
  });
  return isSvg ? q.data ?? null : null;
}

/** What a replaced sheet carried and what it could not: the marks on the sheet it replaced whose items have none on this one. */
export function sheetTray(ctx: JobCtx, view: Row | null, items: ResolvedItem[]): { carried: number; toPlace: ResolvedItem[]; when: string | null } {
  if (!view?.supersedes_view_id) return { carried: 0, toPlace: [], when: null };
  const here = ctx.data.marks.filter((m) => m.view_id === view.id);
  const old = ctx.data.marks.filter((m) => m.view_id === view.supersedes_view_id);
  const placed = new Set(here.map((m) => m.job_item_id as string));
  const toPlace = old
    .filter((m) => !placed.has(m.job_item_id))
    .map((m) => items.find((i) => i.id === m.job_item_id))
    .filter((i): i is ResolvedItem => !!i);
  return { carried: here.filter((m) => m.carried_from_view_id).length, toPlace, when: view.created_at ? String(view.created_at).slice(5, 10) : null };
}

/**
 * A new sheet for the next revision (Replace sheet, on the View head and on the Views table): the old one stays with its own
 * revision, and its marks carry by item number, a region with its extent. Returns the new view's id.
 */
export async function replaceSheet(ctx: JobCtx, userId: string | null, view: Row, picked: File): Promise<string> {
  const file = await uploadFile(picked, 'job', ctx.jobId, userId);
  const fresh = await insertRow('view', {
    job_id: ctx.jobId, file_id: file.id, name: view.name, kind: view.kind, is_primary: view.is_primary, is_overview: view.is_overview, sort: view.sort, supersedes_view_id: view.id,
  });
  if (view.is_primary) await updateRow('view', view.id, { is_primary: false });
  const numberOf = new Map(ctx.data.items.map((i) => [i.id as string, String(i.number ?? '')]));
  const old = ctx.data.marks.filter((m) => m.view_id === view.id).map((m) => ({ number: numberOf.get(m.job_item_id) ?? '', x_pct: Number(m.x_pct), y_pct: Number(m.y_pct), w_pct: m.w_pct === null || m.w_pct === undefined ? null : Number(m.w_pct), h_pct: m.h_pct === null || m.h_pct === undefined ? null : Number(m.h_pct) }));
  const { carried, lost } = carryMarks(old, ctx.data.items.map((i) => ({ id: i.id as string, number: String(i.number ?? '') })));
  if (carried.length) await insertRows('mark', carried.map((m) => ({ ...m, view_id: fresh.id, carried_from_view_id: view.id })));
  toast(`New sheet in place. ${carried.length} ${carried.length === 1 ? 'mark' : 'marks'} carried by item number${lost.length ? `; ${lost.length} to place (${lost.join(', ')})` : ''}.`);
  return fresh.id as string;
}

export function ViewHead({
  ctx, view, walls, onWall, placing, setPlacing, onAdd, onGrid, selecting, setSelecting, showFronts, extra,
}: {
  ctx: JobCtx;
  view: Row | null;
  walls: Row[];
  onWall: (id: string) => void;
  placing: boolean;
  setPlacing: (b: boolean) => void;
  onAdd: () => void;
  onGrid: () => void;
  selecting: boolean;
  setSelecting: (b: boolean) => void;
  showFronts: boolean;
  extra?: React.ReactNode;
}) {
  const s = useSession();
  const canEdit = s.canEdit('screen.item') && !ctx.locked;
  const sheet = useRef<HTMLInputElement>(null);
  const kinds = s.setting<ViewKind[]>('view_kinds', []);
  const revisions = useRows('revision', { eq: { job_id: ctx.jobId } });
  const rev = view?.revision_id ? revisions.rows.find((x) => x.id === view.revision_id) : undefined;

  const onSheet = async (list: FileList | null) => {
    const picked = list?.[0];
    if (!picked || !view) return;
    const fresh = await replaceSheet(ctx, s.user?.id ?? null, view, picked);
    ctx.go({ view: fresh });
    if (sheet.current) sheet.current.value = '';
  };

  return (
    <div className="vhead">
      <span className="lbl">View</span>
      <div className="vpick">
        {walls.length === 0 && <span className="only">No View yet: upload a drawing under Files and choose "Use as view".</span>}
        {walls.map((w) => (
          <button key={w.id} type="button" className={!showFronts && view?.id === w.id ? 'on' : showFronts && view?.id === w.id ? 'on' : ''} title={w.name ?? undefined} onClick={() => onWall(w.id)}>
            {wallShort(w.name)}
          </button>
        ))}
      </div>
      {extra}
      {rev && <span className="tag off" title="A locked revision holds this sheet; Replace sheet makes a new sheet for the next revision">Rev {rev.rev_no}</span>}
      <span className="sp" />
      {view && canEdit && !showFronts && (
        <button type="button" className={`btn${placing ? ' pri' : ''}`} aria-pressed={placing} title={placing ? 'Placing is on: Done placing in the tray turns it off' : 'Place the open item on this sheet, or pick one under Not placed yet; drag a mark to move it'} onClick={() => setPlacing(!placing)}>
          {isOverview(view as any, kinds) ? 'Place spots' : 'Place marks'}
        </button>
      )}
      {view && s.can('screen.item') && (
        <>
          <input ref={sheet} type="file" accept="image/*" className="hide" onChange={(e) => void onSheet(e.target.files)} />
          {canEdit && (
            <button type="button" className="btn" title="Upload the new version of this sheet for the next revision. The old sheet stays with its own revision; its marks carry over by item number." onClick={() => sheet.current?.click()}>
              Replace sheet
            </button>
          )}
        </>
      )}
      {canEdit && (
        <>
          <button type="button" className="btn" onClick={onAdd}>
            + item
          </button>
          <button type="button" className="btn" onClick={onGrid}>
            Add many
          </button>
          <button type="button" className={`btn${selecting ? ' pri' : ''}`} title="Pick several items in the list and edit what they share together" onClick={() => setSelecting(!selecting)}>
            {selecting ? 'Done selecting' : 'Select to edit'}
          </button>
        </>
      )}
    </div>
  );
}

/** The item a mark is placed for: the open item's top-level stop, and an alternate's base (V8: an alternate shares its base's mark). */
export function placeTarget(model: WalkModel, selectedId: string | null): ResolvedItem | null {
  if (!selectedId) return null;
  const top = model.stopOf(selectedId)?.item ?? model.res.items.find((i) => i.id === selectedId) ?? null;
  if (!top) return null;
  if (top.replaces_item_id) return model.res.items.find((i) => i.id === top.replaces_item_id) ?? top;
  return top;
}

/**
 * The tray under the View head: while placing is on, what is being placed and how (the Sheet-replaced tray's mechanism, made general,
 * V4); and after a sheet was replaced, what carried and the marks still to place.
 */
export function PlacingTray({
  ctx, model, view, placing, setPlacing, onPlace, linkTarget, setLinkTarget,
}: {
  ctx: JobCtx;
  model: WalkModel;
  view: Row | null;
  placing: boolean;
  setPlacing: (b: boolean) => void;
  onPlace: (item: ResolvedItem) => void;
  /** On an overview: the view a new spot jumps to. */
  linkTarget: string;
  setLinkTarget: (id: string) => void;
}) {
  const s = useSession();
  const canEdit = s.canEdit('screen.item') && !ctx.locked;
  const kinds = s.setting<ViewKind[]>('view_kinds', []);
  const t = sheetTray(ctx, view, model.res.items);
  const replaced = !!view?.supersedes_view_id && t.toPlace.length > 0;
  if (!view || (!placing && !replaced)) return null;
  const overview = isOverview(view as any, kinds);
  const target = placeTarget(model, ctx.itemId);
  const open = ctx.itemId ? model.res.items.find((i) => i.id === ctx.itemId) ?? null : null;
  const first = t.toPlace[0];
  return (
    <div className="tray">
      {placing ? (
        <>
          <span className="lbl">Placing</span>
          {overview ? (
            <span>
              Spot jumps to{' '}
              <select value={linkTarget} onChange={(e) => setLinkTarget(e.target.value)} aria-label="The view a spot jumps to">
                <option value="">pick a view…</option>
                {ctx.data.views.filter((v) => v.id !== view.id).map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name ?? v.kind ?? 'view'}
                  </option>
                ))}
              </select>{' '}
              then click the sheet. Click a spot to remove it.
            </span>
          ) : target ? (
            <>
              <span className="mkr">{target.number}</span>
              <span>
                {open && open.id !== target.id ? `${open.number} sits where ${target.number} sits. ` : ''}
                Click the sheet where {target.number} sits. <span className="only">drag a mark to move it · press and drag for a region</span>
              </span>
            </>
          ) : (
            <span>
              Open an item, or pick one under Not placed yet, then click the sheet where it sits. <span className="only">drag a mark to move it</span>
            </span>
          )}
          <span className="sp" />
          <button type="button" className="btn" onClick={() => setPlacing(false)}>
            Done placing
          </button>
        </>
      ) : (
        <span className="lbl">Sheet replaced</span>
      )}
      {replaced && (
        <>
          <span>
            New {wallShort(view.name)} sheet{t.when ? `, ${t.when}` : ''} · {t.carried} {t.carried === 1 ? 'mark' : 'marks'} carried by item number · {t.toPlace.length} to place:
          </span>
          {t.toPlace.map((i) => (
            <span key={i.id} className="mkr">
              {i.number}
            </span>
          ))}
          {canEdit && first && (!placing || target?.id !== first.id) && (
            <button type="button" className="btn pri" onClick={() => onPlace(first)}>
              Place {first.number} on the sheet
            </button>
          )}
        </>
      )}
    </div>
  );
}

type Drag = { id: string; x0: number; y0: number; x: number; y: number; moved: boolean };
type Rubber = { x0: number; y0: number; x: number; y: number };

/** The sheet with its marks. `walk` draws the check circles, the next flag and the hollow marks of items the open build does not carry. */
export function Picture({
  ctx, model, view, selectedId, placing, walk, nextId, onPick, linkTarget,
}: {
  ctx: JobCtx;
  model: WalkModel;
  view: Row;
  selectedId: string | null;
  placing: boolean;
  walk: boolean;
  nextId: string | null;
  onPick: (id: string) => void;
  /** On an overview while placing: the view a new spot jumps to. */
  linkTarget?: string;
}) {
  const s = useSession();
  const canEdit = s.canEdit('screen.item') && !ctx.locked;
  const kinds = s.setting<ViewKind[]>('view_kinds', []);
  const overview = isOverview(view as any, kinds);
  const file = useRows('file', { eq: { id: view.file_id }, enabled: !!view.file_id }).rows[0];
  const url = useSignedUrl(file?.storage_path);
  const aspect = useSvgAspect(url, /svg/i.test(String(file?.mime ?? file?.filename ?? '')));
  const links = useRows('view_link', { eq: { view_id: view.id } });
  const pic = useRef<HTMLDivElement>(null);
  const byId = useMemo(() => new Map(model.res.items.map((i) => [i.id, i])), [model.res.items]);
  const marks = ctx.data.marks.filter((m) => m.view_id === view.id);
  const target = placing ? placeTarget(model, selectedId) : null;
  const selTop = selectedId ? model.stopOf(selectedId)?.item ?? null : null;
  const aiChanged = new Set(ctx.data.items.filter((x) => x.unverified_since).map((x) => x.id as string));
  const [drag, setDrag] = useState<Drag | null>(null);
  const [rubber, setRubber] = useState<Rubber | null>(null);
  const live = placing && canEdit;

  /** A pointer's place on the sheet, in percent across and down, held to the sheet. */
  const at = (e: { clientX: number; clientY: number }): { x: number; y: number } => {
    const box = pic.current!.getBoundingClientRect();
    return {
      x: Number(Math.max(0, Math.min(100, ((e.clientX - box.left) / box.width) * 100)).toFixed(2)),
      y: Number(Math.max(0, Math.min(100, ((e.clientY - box.top) / box.height) * 100)).toFixed(2)),
    };
  };
  const write = async (itemId: string, x: number, y: number, region: { w_pct: number; h_pct: number } | null) => {
    await upsertRow('mark', { view_id: view.id, job_item_id: itemId, x_pct: x, y_pct: y, w_pct: region?.w_pct ?? null, h_pct: region?.h_pct ?? null, placed_by: s.user?.id ?? null, carried_from_view_id: null }, 'view_id,job_item_id');
  };

  // placing on the sheet itself: a press is the start of a point or a region; the release decides which
  const sheetDown = (e: React.PointerEvent) => {
    if (!live || !pic.current || e.button !== 0) return;
    if ((e.target as HTMLElement).closest('.mk')) return;
    if (overview) return; // a spot is placed on click
    if (!target || !target.stored) return;
    e.preventDefault(); // no text selection while a region is drawn
    const p = at(e);
    setRubber({ x0: p.x, y0: p.y, x: p.x, y: p.y });
    pic.current.setPointerCapture(e.pointerId);
  };
  const sheetMove = (e: React.PointerEvent) => {
    if (!pic.current) return;
    if (drag) {
      const p = at(e);
      setDrag({ ...drag, x: p.x, y: p.y, moved: drag.moved || Math.abs(p.x - drag.x0) > 0.4 || Math.abs(p.y - drag.y0) > 0.4 });
      return;
    }
    if (rubber) {
      const p = at(e);
      setRubber({ ...rubber, x: p.x, y: p.y });
    }
  };
  const sheetUp = async (e: React.PointerEvent) => {
    if (!pic.current) return;
    if (drag) {
      const d = drag;
      setDrag(null);
      if (!d.moved) return;
      const m = marks.find((x) => x.id === d.id);
      if (!m) return;
      const region = m.w_pct !== null && m.w_pct !== undefined ? { w_pct: Number(m.w_pct), h_pct: Number(m.h_pct) } : null;
      // a region keeps its size on the sheet as it moves
      const x = region ? Math.max(region.w_pct / 2, Math.min(100 - region.w_pct / 2, d.x)) : d.x;
      const y = region ? Math.max(region.h_pct / 2, Math.min(100 - region.h_pct / 2, d.y)) : d.y;
      await write(m.job_item_id, Number(x.toFixed(2)), Number(y.toFixed(2)), region);
      return;
    }
    if (rubber && target) {
      const r = rubber;
      setRubber(null);
      const w = Math.abs(r.x - r.x0);
      const h = Math.abs(r.y - r.y0);
      // a short press is a point; a drag of any size is a region around its centre
      if (w < 1.5 && h < 1.5) await write(target.id, r.x0, r.y0, null);
      else await write(target.id, Number(((r.x + r.x0) / 2).toFixed(2)), Number(((r.y + r.y0) / 2).toFixed(2)), { w_pct: Number(Math.max(1, w).toFixed(2)), h_pct: Number(Math.max(1, h).toFixed(2)) });
    }
    void e;
  };
  // a spot on an overview is placed on click, as before
  const spot = async (e: React.MouseEvent) => {
    if (!live || !overview || !pic.current || !linkTarget) return;
    if ((e.target as HTMLElement).closest('.mk')) return;
    const p = at(e);
    await upsertRow('view_link', { view_id: view.id, target_view_id: linkTarget, x_pct: p.x, y_pct: p.y, label: ctx.data.views.find((v) => v.id === linkTarget)?.name ?? null }, 'view_id,target_view_id');
  };
  // a mark dragged while placing is on
  const markDown = (e: React.PointerEvent, m: Row) => {
    if (!live || e.button !== 0 || !pic.current) return;
    e.stopPropagation();
    e.preventDefault(); // no text selection while a mark is dragged
    const p = at(e);
    setDrag({ id: m.id as string, x0: p.x, y0: p.y, x: Number(m.x_pct), y: Number(m.y_pct), moved: false });
    pic.current.setPointerCapture(e.pointerId);
  };

  // alternates (V8): an item standing in for another has no mark of its own and is drawn dashed where its base sits
  const alternates = model.res.items.filter((i) => i.replaces_item_id && !i.parent_item_id && !marks.some((m) => m.job_item_id === i.id));

  const posOf = (m: Row) => {
    if (drag && drag.id === m.id && drag.moved) {
      const dx = drag.x - drag.x0;
      const dy = drag.y - drag.y0;
      return { x: Number(m.x_pct) + dx, y: Number(m.y_pct) + dy };
    }
    return { x: Number(m.x_pct), y: Number(m.y_pct) };
  };

  return (
    <>
      <div className="draw">
        <div
          className={`pic${url ? ' view' : ''}${live ? ' placing' : ''}`}
          ref={pic}
          onClick={(e) => void spot(e)}
          onPointerDown={sheetDown}
          onPointerMove={sheetMove}
          onPointerUp={(e) => void sheetUp(e)}
          onPointerCancel={() => {
            setDrag(null);
            setRubber(null);
          }}
          style={{ cursor: live ? 'crosshair' : 'default' }}
        >
          {url ? <img src={url} alt={view.name ?? 'View'} draggable={false} style={aspect ? { aspectRatio: String(aspect), width: '100%', height: 'auto' } : undefined} /> : <div className="blank">Loading the sheet…</div>}
          {[...marks].sort((a, b) => Number(a.job_item_id === selectedId || a.job_item_id === selTop?.id) - Number(b.job_item_id === selectedId || b.job_item_id === selTop?.id)).map((m) => {
            const item = byId.get(m.job_item_id);
            if (!item) return null;
            const on = selectedId === item.id || selTop?.id === item.id || (target?.id === item.id && !!selectedId);
            const off = model.notIn(item);
            const stop = model.stopOf(item.id);
            const checked = walk && stop ? model.stateOf(stop) === 'checked' : false;
            const region = m.w_pct !== null && m.w_pct !== undefined;
            const p = posOf(m);
            const cls = `mk${region ? ' region' : ''}${on ? ' hl' : ''}${live ? ' placing' : ''}${drag && drag.id === m.id && drag.moved ? ' drag' : ''}${aiChanged.has(item.id) ? ' un' : ''}${item.add_on_id && !item.replaces_item_id ? ' ad' : ''}${walk && off ? ' off' : ''}${checked ? ' ok' : ''}`;
            const style: React.CSSProperties = region
              ? { left: `${p.x - Number(m.w_pct) / 2}%`, top: `${p.y - Number(m.h_pct) / 2}%`, width: `${m.w_pct}%`, height: `${m.h_pct}%` }
              : { left: `${p.x}%`, top: `${p.y}%` };
            return (
              <span
                key={m.id}
                className={cls}
                style={style}
                title={`${item.number} · ${item.name}${region ? ' · a region: its whole area opens the item' : ''}${off ? ` · not in ${ctx.build?.name ?? 'this build'}` : ''}${aiChanged.has(item.id) ? ' · changed by an AI employee, not yet verified' : ''}${m.carried_from_view_id ? ' · carried from the sheet this one replaced' : ''}${live ? ' · drag to move' : ''}`}
                aria-pressed={on}
                onPointerDown={(e) => markDown(e, m)}
                {...pressable((e) => {
                  if (live) return;
                  e.stopPropagation();
                  onPick(item.id);
                })}
              >
                <b className="mkno">
                  {item.number}
                  {checked && <i className="mkck" aria-label="checked" />}
                  {walk && nextId === item.id && <i className="mknext">next</i>}
                </b>
              </span>
            );
          })}
          {alternates.map((alt) => {
            const baseMark = marks.find((m) => m.job_item_id === alt.replaces_item_id);
            if (!baseMark) return null;
            const on = selectedId === alt.id || selTop?.id === alt.id;
            const off = model.notIn(alt);
            const p = posOf(baseMark);
            return (
              <span
                key={`alt-${alt.id}`}
                className={`mk alt${on ? ' hl' : ''}${walk && off ? ' off' : ''}`}
                style={{ left: `${p.x}%`, top: `${p.y}%` }}
                title={`${alt.number} · ${alt.name} · stands in for ${byId.get(alt.replaces_item_id!)?.number ?? 'the item here'} and sits where it sits`}
                aria-pressed={on}
                {...pressable((e) => {
                  if (live) return;
                  e.stopPropagation();
                  onPick(alt.id);
                })}
              >
                <b className="mkno">{alt.number}</b>
              </span>
            );
          })}
          {links.rows.map((l) => {
            const t = ctx.data.views.find((v) => v.id === l.target_view_id);
            return (
              <span
                key={l.id}
                className="mk spot"
                style={{ left: `${l.x_pct}%`, top: `${l.y_pct}%` }}
                title={`Jump to ${t?.name ?? 'view'}`}
                {...pressable((e) => {
                  e.stopPropagation();
                  if (live) {
                    if (window.confirm(`Remove the spot that jumps to ${t?.name ?? 'this view'}?`)) void deleteRow('view_link', l.id);
                    return;
                  }
                  if (t) ctx.go({ view: t.id as string });
                })}
              >
                → {l.label ?? t?.name ?? 'view'}
              </span>
            );
          })}
          {rubber && (Math.abs(rubber.x - rubber.x0) >= 1.5 || Math.abs(rubber.y - rubber.y0) >= 1.5) && (
            <span className="rubber" style={{ left: `${Math.min(rubber.x, rubber.x0)}%`, top: `${Math.min(rubber.y, rubber.y0)}%`, width: `${Math.abs(rubber.x - rubber.x0)}%`, height: `${Math.abs(rubber.y - rubber.y0)}%` }}>
              {target?.number}
            </span>
          )}
        </div>
      </div>
    </>
  );
}

/** Whole room has no sheet of its own. */
export function RoomNote() {
  return (
    <div className="roomnote">
      <b>Whole room has no drawing</b>
      <span>The items marked on no View (toe kicks that span walls, countertops, job-wide items, the vendor orders), the job route and the Job BOM.</span>
    </div>
  );
}
